import React, { useRef, useState } from "react";
import {
  BaseLogo,
  getLogoColors,
  isDigital,
  hasUserPendingApprovalStatus,
} from "../../../../core/models/entities/BaseLogo";
import { Organization } from "../../../../core/models/entities/Organization";
import IconButton from "../../../components/LogoButton/IconButton";
import { DigitalColorElement } from "../../../components/Logo/DigitalColorElement";

// Helper function to get vendor info from customer
function getVendorInfo(customer: Organization): {
  name: string;
  navNumber: string;
} {
  const vendor = customer.parent || customer;
  return {
    name: vendor.name,
    navNumber: vendor.navNumber,
  };
}
import { VectorColorElement } from "../../../components/Logo/VectorColorElement";
import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import { LogoSize } from "../../../components/Logo/LogoSize";
import Tooltip from "@mui/material/Tooltip";
import { LogoPrices } from "../../../components/Logo/LogoPrices";
import { formatDate } from "../../../../utils/functions/DateUtil";
import { LogoStatusElement } from "../../../components/Logo/LogoStatusElement";
import { ColumnSettings } from "../../../components/GenericTable/SubComponents/ListFrame";
import { SinglePointLogoStorage } from "../../../components/Logo/LogoStorage";
import { StorageUpdatedAt } from "../../../components/Logo/StorageUpdatedAt";
import { enviroment } from "../../../../enviroment";
import { ConfirmationGuard } from "../../../components/ConfirmationGuard";
import { LogoTransferMethod } from "../../../components/Logo/LogoTransferMethod";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import {
  getLogoState,
  LogoState,
} from "../vmHooks/useFunctionalListLogosViewModel";
import { LogoImage } from "../../../components/Logo/LogoImage";
import { AwaitingImageBadge } from "../../../components/Logo/AwaitingImageBadge";
import {
  ProductPendingBadge,
  ProductPendingLock,
} from "../../../components/Logo/ProductPendingBadge";
import {
  isLogoActionLocked,
  isLogoProductPending,
  isLogoUploadPending,
  LogoActionLockVariant,
  logoActionLockVariant,
} from "../../../../utils/functions/productPending";
import {
  ApprovalVoting,
  Verdict,
} from "../../../components/ApprovalVoting/approval-voting";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import {
  AddToCartIcon,
  EditIcon,
  NoteIcon,
  TrashcanCrossIcon,
} from "../../../../resources/icons/Icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList } from "@fortawesome/free-solid-svg-icons";
import { Stepper } from "../../../components/Buttons/Stepper";
import {
  getLogoMinQuantity,
  isValidLogoQuantity,
} from "../../../../utils/functions/QuantityUtil";
import CustomerTooltip from "../../../components/Organization/CustomerTooltip";
import { useAddLogoToOrderDialog } from "../../../components/Dialogs/add-logo-to-design-order-dialog";
import { useLogoContext } from "../../design-editor2/contexts/logo-options.context";
import { CircularProgress } from "@mui/material";
import { useCartHidden } from "../../../../hooks/useCartHidden";
import { CopyNumberButton } from "../../../components/CopyToClipboard/copy-number-button.component";
import { useTranslation } from "react-i18next";
import { isEmbroideryTransferType } from "../../../../utils/systemColors";
import { transferMethodLabel } from "../../../../utils/functions/TransferMethodUtil";

interface LogoListItemComponent {
  logo: BaseLogo;
  columns: ColumnSettings;
  onSeeRejectNote?: (logo: BaseLogo) => void;
  onAddToCart: (logo: BaseLogo, quantity: number) => void;
  onEditLogo: (logo: BaseLogo) => void;
  onDeleteLogo: (logo: BaseLogo) => void;
  onInitiateApproval: (logo: BaseLogo) => void;
  onPlaceVerdict: (logo: BaseLogo, verdict: Verdict) => void;
  onRejectAndDelete?: (logo: BaseLogo, note?: string) => void;
  onViewDesigns: (logo: BaseLogo) => void;
  updatingLogoId?: number;
  // Folder drag-and-drop (vendor/admin only): when set, the row becomes an
  // HTML5 drag source; the callback fills the dataTransfer payload (see
  // FolderBar/folder-bar.component.tsx for the drop-target side).
  onDragStartLogo?: (
    logo: BaseLogo,
    e: React.DragEvent<HTMLDivElement>,
  ) => void;
}

export function LogoRowItem({
  logo,
  columns,
  onSeeRejectNote,
  onAddToCart,
  onEditLogo,
  onDeleteLogo,
  onInitiateApproval,
  onPlaceVerdict,
  onRejectAndDelete,
  onViewDesigns,
  updatingLogoId,
  onDragStartLogo,
}: LogoListItemComponent) {
  const hasCustomizableQuantity = ["NumberLogo", "NameLogo"].includes(
    logo?.logoType
  );
  const minQuantity = getLogoMinQuantity(logo);
  const [quantityForOrder, setQuantityForOrder] = useState<number>(
    hasCustomizableQuantity ? 0 : minQuantity
  );
  const { options } = useLogoContext();
  const colorOptions = options.colors;
  const colorsEmbroidery = options.colorsEmbroidery;
  const { role } = useProfileContext();
  const { t } = useTranslation();

  // HACK: The performance is harrowing. These dialogs need to go...
  const { component, open } = useAddLogoToOrderDialog(undefined, logo);

  function SeeRejectNote(e?: React.MouseEvent<HTMLElement>) {
    e?.stopPropagation();
    onSeeRejectNote?.call(null, logo);
  }

  function EditLogo(e?: React.MouseEvent<HTMLElement>) {
    e?.stopPropagation();
    onEditLogo(logo);
  }

  function AddToCart(e?: React.MouseEvent<HTMLElement>) {
    e?.stopPropagation();
    // Id logo is not an image logo, we don't need to handle adding to cart here,
    //  because the AddLogoToOrderDialog component handles this internally...
    if (hasCustomizableQuantity) open();
    // Image logos are added directly to cart from the list.
    else onAddToCart(logo, quantityForOrder);
  }

  async function Delete(e?: React.MouseEvent<HTMLElement>) {
    e?.stopPropagation();
    onDeleteLogo(logo);
  }

  function handleStartApproval(e?: React.MouseEvent<HTMLElement>) {
    e?.stopPropagation();
    onInitiateApproval(logo);
  }

  const overallState = getLogoState(logo);
  const colors = getLogoColors(logo, colorOptions, colorsEmbroidery);

  const isUpdating =
    updatingLogoId === logo.id && overallState === LogoState.Draft;

  let colorsElements = isDigital(logo) ? (
    <DigitalColorElement />
  ) : (
    <div className="grid grid-rows-4 grid-flow-col">
      {colors.map((co) =>
        co.isIndigo ? (
          <DigitalColorElement key={co.id} showText={true} text={co.name} />
        ) : (
          <VectorColorElement
            key={co.id}
            showName={true}
            color={co}
            embroideryMethod={isEmbroideryTransferType(
              logo.transferMethod?.type,
            )}
          />
        )
      )}
    </div>
  );

  // True while (and briefly after) an HTML5 drag: suppresses the row-click
  // navigation that lives on the surrounding ListItemFrame. Browsers do not
  // normally fire click after a drag, but this guards the edge cases.
  const isDragging = useRef(false);

  const dragProps = onDragStartLogo
    ? {
        draggable: true,
        onDragStart: (e: React.DragEvent<HTMLDivElement>) => {
          isDragging.current = true;
          onDragStartLogo(logo, e);
        },
        onDragEnd: () => {
          setTimeout(() => {
            isDragging.current = false;
          }, 150);
        },
        onClickCapture: (e: React.MouseEvent<HTMLDivElement>) => {
          if (isDragging.current) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
      }
    : {};

  return (
    <StrucRow className={"h-[100px] items-start py-4"} {...dragProps}>
      <StrucCol
        size={columns["thumbnail"].weight}
        className={"items-center !justify-around space-y-2 h-full"}
      >
        {isUpdating ? (
          <div
            className={
              "flex align-middle items-center h-[74px] w-[74px] justify-center mr-5 bg-white rounded-md border border-gray-100"
            }
          >
            <CircularProgress size={14} />
          </div>
        ) : (
          <div
            className={"flex align-middle items-center max-h-[80px] w-fit mr-5"}
          >
            <LogoImage
              logo={logo}
              useHighRes={false}
              scaleNameByHeight
              className={
                "bg-white hover:rounded hover:border hover:p-2 max-h-[74px] origin-left hover:scale-[3] hover:ease-out transition-all duration-150 ease-out"
              }
            />
          </div>
        )}
      </StrucCol>
      <StrucCol className="space-y-0.5" size={columns["product.sku"].weight}>
        <div className="flex items-center">
          <Tooltip
            title={
              logo.product?.sku?.startsWith("UPLOAD_")
                ? ""
                : logo.product?.sku ?? "-"
            }
            placement={"top-start"}
          >
            <span
              className={
                "font-bold whitespace-nowrap overflow-x-hidden overflow-ellipsis min-w-0"
              }
              onClick={(e) => e.stopPropagation()}
            >
              {logo.product?.sku?.startsWith("UPLOAD_")
                ? "-"
                : logo.product?.sku ?? "-"}
            </span>
          </Tooltip>
          {logo.product?.sku && !logo.product.sku.startsWith("UPLOAD_") && (
            <CopyNumberButton value={logo.product.sku} className="ml-1" />
          )}
        </div>
        <LogoSize
          logo={logo}
          format={
            logo.logoType === "NumberLogo"
              ? "HYYmm"
              : logo.logoType === "NameLogo"
              ? "HYYmm"
              : "WWxYYmm"
          }
        />
      </StrucCol>
      <StrucCol
        className={"space-y-0.5"}
        size={columns[nameOf<BaseLogo>((x) => x.title)].weight}
      >
        <Tooltip title={logo.title} placement={"top-start"}>
          <span
            className={
              "font-bold whitespace-nowrap overflow-x-hidden overflow-ellipsis"
            }
            onClick={(e) => e.stopPropagation()}
          >
            {logo.title}
          </span>
        </Tooltip>
        <span className="text-xs capitalize">
          {/* transferMethodLabel: the same text the customer edit screen's
              Logos section shows; tolerant of rows without a transfer
              method (foreign-org rows), see TransferMethodUtil. */}
          {transferMethodLabel(logo.transferMethod, t)}
        </span>
      </StrucCol>
      <StrucCol size={columns["colors"].weight}>{colorsElements}</StrucCol>
      {columns["vendor"] && (
        <StrucCol size={columns["vendor"].weight}>
          {logo.customer && (
            <Tooltip
              title={`${getVendorInfo(logo.customer).name} (${
                getVendorInfo(logo.customer).navNumber
              })`}
              placement={"top-start"}
            >
              <span className="font-bold whitespace-nowrap overflow-x-hidden overflow-ellipsis">
                {getVendorInfo(logo.customer).name}
              </span>
            </Tooltip>
          )}
        </StrucCol>
      )}
      <StrucCol size={columns[nameOf<BaseLogo>((x) => x.customer)].weight}>
        {!logo.isForAllCustomers && logo.customer && logo.customer.type !== "vendor" && (
          <CustomerTooltip customer={logo.customer} />
        )}
      </StrucCol>
      <StrucCol size={columns["priceSort"].weight}>
        {/* "Prices on their way" (flag productPendingEnabled): the badge
            replaces the (old or missing) prices while the API worker still
            owes the product. isLogoProductPending is always false with the
            flag off, so the cell is exactly today's. */}
        {isLogoProductPending(logo) ? (
          <ProductPendingBadge
            pending={true}
            sinceUtc={logo.productPendingSinceUtc}
          />
        ) : columns["priceSort"].displayValue && logo.product && overallState !== LogoState.Draft ? (
          <LogoPrices logo={logo} />
        ) : (
          <span></span>
        )}
      </StrucCol>
      <StrucCol size={columns["product.storagecount"].weight}>
        <SinglePointLogoStorage logo={logo} showRefreshButton={true} manualRefresh />
        {enviroment.storageFreshnessEnabled && (
          <StorageUpdatedAt storageUpdatedAtUtc={logo.storageUpdatedAtUtc} />
        )}
      </StrucCol>
      <StrucCol size={columns[nameOf<BaseLogo>((x) => x.createdAtUtc)].weight}>
        {formatDate(logo.CreationTime || logo.createdAtUtc)}
      </StrucCol>
      <StrucCol size={columns["stateFilter"].weight}>
        <LogoStatusElement logo={logo} />
        {logo.awaitingImage && <AwaitingImageBadge className="mt-1" />}
        {/* "Preparing logo…" (flag cloneInstantEnabled): the clone still
            awaits its own supplier identity. Always false with the flag off
            = no element. */}
        {isLogoUploadPending(logo) && (
          <ProductPendingBadge
            variant="upload"
            pending={true}
            sinceUtc={logo.uploadPendingSinceUtc}
            className="mt-1"
          />
        )}
      </StrucCol>
      <StrucCol
        size={columns["actions"].weight}
        className={"items-start !justify-start space-y-2 h-full"}
      >
        {(() => {
          // Check if current user has pending approval status first
          if (hasUserPendingApprovalStatus(logo.approval, role as number)) {
            return (
              <PendingActions
                deleteLogo={Delete}
                editLogo={EditLogo}
                logo={logo}
                onPlaceVerdict={onPlaceVerdict}
                onRejectAndDelete={onRejectAndDelete}
              />
            );
          }

          // Very bad way to render conditional actions, but it is better than freezing them in time during rendering pipeline.
          switch (overallState) {
            case LogoState.Rejected:
              return (
                <RejectedActions
                  logo={logo}
                  deleteLogo={Delete}
                  seeRejectionNote={SeeRejectNote}
                />
              );
            case LogoState.Committed:
              return (
                <CommittedActions
                  handleStartApproval={handleStartApproval}
                  productPending={isLogoActionLocked(logo)}
                  lockVariant={logoActionLockVariant(logo)}
                />
              );
            case LogoState.Approved:
              return (
                <ApprovedActions
                  logo={logo}
                  role={role}
                  addToCart={AddToCart}
                  hasCustomizableQuantity={hasCustomizableQuantity}
                  quantityForOrder={quantityForOrder}
                  setQuantityForOrder={setQuantityForOrder}
                  onViewDesigns={onViewDesigns}
                />
              );
            case LogoState.Pending:
              return (
                <PendingActions
                  deleteLogo={Delete}
                  editLogo={EditLogo}
                  logo={logo}
                  onPlaceVerdict={onPlaceVerdict}
                  onRejectAndDelete={onRejectAndDelete}
                />
              );
            case LogoState.Draft:
              return (
                <DraftActions
                  logo={logo}
                  deleteLogo={Delete}
                  editLogo={EditLogo}
                />
              );
          }
        })()}
        {component}
      </StrucCol>
    </StrucRow>
  );
}

function RejectedActions({
  logo,
  deleteLogo,
  seeRejectionNote,
}: {
  logo: BaseLogo;
  deleteLogo: (e?: React.MouseEvent<HTMLElement>) => void;
  seeRejectionNote?: (e?: React.MouseEvent<HTMLElement>) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <IconButton
        key={"see-note"}
        onClick={seeRejectionNote}
        icon={<NoteIcon />}
      >
        {t("logoList.seeNote")}
      </IconButton>
      {String(logo.Classification) !== "11" && (
        <ConfirmationGuard
          anchor={
            <IconButton icon={<TrashcanCrossIcon />} variant="error">
              {t("delete")}
            </IconButton>
          }
          text={t("logoList.deleteLogoConfirm")}
          onAccept={deleteLogo}
        />
      )}
    </>
  );
}

function CommittedActions({
  handleStartApproval,
  productPending,
  lockVariant,
}: {
  handleStartApproval: (e?: React.MouseEvent<HTMLElement>) => void;
  // "Prices on their way" (flag productPendingEnabled) / "Preparing logo…"
  // (flag cloneInstantEnabled): approval-start is locked with a tooltip
  // while either worker still owes the logo. False = as today.
  productPending?: boolean;
  lockVariant?: LogoActionLockVariant;
}) {
  const { t } = useTranslation();
  const locked = productPending === true;
  return (
    <ProductPendingLock locked={locked} variant={lockVariant ?? "product"}>
      <IconButton
        key={"see-note"}
        onClick={locked ? undefined : handleStartApproval}
        disabled={locked || undefined}
        icon={<NoteIcon />}
      >
        {" "}
        {t("logoList.startApproval")}
      </IconButton>
    </ProductPendingLock>
  );
}

function ApprovedActions({
  logo,
  role,
  hasCustomizableQuantity,
  setQuantityForOrder,
  quantityForOrder,
  addToCart,
  onViewDesigns,
}: {
  logo: BaseLogo;
  role?: Role;
  hasCustomizableQuantity: Boolean;
  quantityForOrder: number;
  setQuantityForOrder: (value: number) => void;
  addToCart: (e?: React.MouseEvent<HTMLElement>) => void;
  onViewDesigns: (logo: BaseLogo) => void;
}) {
  const minQuantity = getLogoMinQuantity(logo);
  const cartHidden = useCartHidden();
  const { t } = useTranslation();
  if (role === Role.Admin) {
    return <></>;
  }

  // Check if logo has transfer type "transfer". Optional chaining: foreign-org
  // rows (act-as) can lack a transfer method — see the type cell above.
  const isTransferType =
    logo.transferMethod?.type?.toLowerCase() === "transfer";

  // When prices/cart are hidden, swap Add to Order for View Designs so the row isn't empty.
  if (!isTransferType || cartHidden) {
    return (
      <IconButton
        key={"view-designs"}
        onClick={() => onViewDesigns(logo)}
        icon={<FontAwesomeIcon icon={faList} />}
      >
        {t("logoList.viewDesigns")}
      </IconButton>
    );
  }

  // "Prices on their way" (flag productPendingEnabled) / "Preparing logo…"
  // (flag cloneInstantEnabled): no add-to-order while either worker still
  // owes the logo. False = as today.
  const productPending = isLogoActionLocked(logo);

  // For transfer type logos, show the original Add to Cart functionality
  return (
    <>
      {!hasCustomizableQuantity && (
        <Stepper
          onValueChange={setQuantityForOrder}
          min={1}
          value={quantityForOrder}
        />
      )}
      <ProductPendingLock
        locked={productPending}
        className="inline-block"
        variant={logoActionLockVariant(logo) ?? "product"}
      >
        <IconButton
          key={"add-order"}
          disabled={
            (!hasCustomizableQuantity &&
              !isValidLogoQuantity(logo, quantityForOrder)) ||
            productPending
          }
          onClick={productPending ? undefined : addToCart}
          icon={<AddToCartIcon />}
          variant="success"
        >
          {t("logoList.addToOrder")}
        </IconButton>
      </ProductPendingLock>
    </>
  );
}

function DraftActions({
  logo,
  editLogo,
  deleteLogo,
}: {
  logo: BaseLogo;
  editLogo: (e?: React.MouseEvent<HTMLElement>) => void;
  deleteLogo: (e?: React.MouseEvent<HTMLElement>) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <IconButton
        key={"approve"}
        onClick={editLogo}
        icon={<EditIcon />}
        variant="success"
      >
        {t("edit")}
      </IconButton>
      {String(logo.Classification) !== "11" && (
        <ConfirmationGuard
          anchor={
            <IconButton icon={<TrashcanCrossIcon />} variant="error">
              {t("delete")}
            </IconButton>
          }
          text={t("logoList.deleteLogoConfirm")}
          onAccept={deleteLogo}
        />
      )}
    </>
  );
}

function PendingActions({
  logo,
  editLogo,
  deleteLogo,
  onPlaceVerdict,
  onRejectAndDelete,
}: {
  onPlaceVerdict: (logo: BaseLogo, verdict: Verdict) => void;
  onRejectAndDelete?: (logo: BaseLogo, note?: string) => void;
  editLogo: (e?: React.MouseEvent<HTMLElement>) => void;
  deleteLogo: (e?: React.MouseEvent<HTMLElement>) => void;
  logo: BaseLogo;
}) {
  const { role } = useProfileContext();
  const approvalGroup = logo.approval;
  // Replicated logic from ApprovalVoting component, becauase inside the component, it is too late to fallback...
  if (
    (role === Role.EndUser &&
      (!approvalGroup?.customer?.approver ||
        approvalGroup?.customer?.status === 0)) ||
    (role === Role.Vendor &&
      (!approvalGroup?.vendor?.approver || approvalGroup?.vendor?.status === 0))
  ) {
    return (
      <ApprovalVoting
        placeVerdict={(verdict) => onPlaceVerdict(logo, verdict)}
        approvalGroup={logo.approval!}
        rejectDelete={(reason) => onRejectAndDelete?.call(null, logo, reason)}
        // "Prices on their way" (flag productPendingEnabled) / "Preparing
        // logo…" (flag cloneInstantEnabled): Approve locked while either
        // worker still owes the logo. False = as today.
        productPending={isLogoActionLocked(logo)}
        lockVariant={logoActionLockVariant(logo)}
      />
    );
  }
  return (
    <DraftActions logo={logo} deleteLogo={deleteLogo} editLogo={editLogo} />
  );
}
